import { WIDGET_CONTENT, widgetPath } from '../../content/widgets.js';
import { CONNECT_SOURCES } from '../../content/sources.js';
import { sourcePagePath } from '../../content/integrations.js';
import { PlayJet } from '../mascot/scenes.js';
import { Container } from '../ui/layout.js';
import { Heading, Text } from '../ui/type.js';
import { ButtonLink, TextLink } from '../ui/button.js';

/**
 * The page for an address that is not here.
 *
 * A search-led site gets more of these than most: links go stale, a slug
 * changes, somebody types a URL from a screenshot. The person seeing this
 * usually wanted something specific and nearly found it, so the useful thing is
 * a short list of the places they were most likely heading — not an
 * illustration of a lost astronaut and a button back to the homepage.
 *
 * Deliberately quiet. No giant "404" set in display type: the number is not
 * the point, and it is the one piece of information the visitor cannot act on.
 */
export function NotFound() {
  return (
    <Container width="text" className="py-20 md:py-28">
      {/*
        Jet, lost: a dashed flight path that loops back on itself, and Jet
        at the end of it looking about. The status code stays out of it —
        the visitor needs to know where to go next, not a number — and this
        is the one place on the site where Jet looks puzzled rather than
        pleased. Pressing it still gets a cheer.
      */}
      <div aria-hidden className="relative mb-6 h-36 w-72 select-none sm:h-40 sm:w-80">
        <svg viewBox="0 0 320 160" className="absolute inset-0 size-full" fill="none">
          <path
            d="M8 128 C60 128 70 40 120 52 C170 64 120 128 92 104 C64 80 150 24 214 76"
            stroke="var(--color-brand)"
            strokeOpacity="0.35"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="2 9"
          />
        </svg>
        <PlayJet mood="think" className="absolute top-2 right-6 w-28 sm:w-32" />
      </div>

      <p className="eyebrow text-ink-400">Page not found</p>

      <Heading level={1} size="display" className="mt-4">
        That page isn’t here.
      </Heading>

      <Text size="lead" className="mt-5">
        It may have moved, or the link that sent you here may be out of date.
        Here is where most people are going.
      </Text>

      <div className="mt-10 grid gap-10 sm:grid-cols-2">
        <div>
          <h2 className="text-caption font-semibold text-ink-950">
            Show reviews from
          </h2>
          <ul className="mt-3.5 space-y-2.5">
            {CONNECT_SOURCES.map((source) => (
              <li key={source.id}>
                <TextLink to={sourcePagePath(source.id)} className="text-body-sm">
                  {source.name} reviews
                </TextLink>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="text-caption font-semibold text-ink-950">Widget layouts</h2>
          <ul className="mt-3.5 space-y-2.5">
            {WIDGET_CONTENT.map((widget) => (
              <li key={widget.slug}>
                <TextLink to={widgetPath(widget)} className="text-body-sm">
                  {widget.seoName}
                </TextLink>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-12">
        <ButtonLink to="/" variant="secondary">
          Back to the homepage
        </ButtonLink>
      </div>
    </Container>
  );
}
